import { c as _c } from "react/compiler-runtime";
import dayjs, { type Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
dayjs.extend(duration);
type CountdownResult = {
  text: string;
  isEnd: boolean;
  days: number;
  daysText: string;
  hoursText: string;
  minutesText: string;
  secondsText: string;
  hours: number;
  minutes: number;
  seconds: number;
  hoursFormatted: string;
  minutesFormatted: string;
  secondsFormatted: string;
};
type CountdownOptions = {
  updateIntervalSeconds?: number;
  showSeconds?: boolean;
  padHoursMinutes?: boolean;
  showDays?: boolean;
};
function formatCountdown(date: string | Date | Dayjs | null | undefined, showSeconds: boolean, padHoursMinutes: boolean, showDays: boolean = true, t?: ReturnType<typeof useTranslations>): CountdownResult {
  const formatNumber = (num: number): string => {
    return padHoursMinutes ? String(num).padStart(2, '0') : String(num);
  };
  if (!date) {
    return {
      text: '',
      isEnd: true,
      days: 0,
      daysText: t?.('d') || 'd',
      hoursText: t?.('h') || 'h',
      minutesText: t?.('m') || 'm',
      secondsText: t?.('s') || 's',
      hours: 0,
      minutes: 0,
      seconds: 0,
      hoursFormatted: formatNumber(0),
      minutesFormatted: formatNumber(0),
      secondsFormatted: formatNumber(0)
    };
  }
  const diff = dayjs(date).diff(dayjs());
  if (diff <= 0) {
    return {
      text: '',
      isEnd: true,
      daysText: t?.('d') || 'd',
      hoursText: t?.('h') || 'h',
      minutesText: t?.('m') || 'm',
      secondsText: t?.('s') || 's',
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      hoursFormatted: formatNumber(0),
      minutesFormatted: formatNumber(0),
      secondsFormatted: formatNumber(0)
    };
  }
  const d = dayjs.duration(diff);
  const days = Math.floor(d.asDays());
  const hours = d.hours();
  const minutes = d.minutes();
  const seconds = d.seconds();
  const parts: string[] = [];
  if (showDays && days > 0) {
    parts.push(`${days}${t?.('d') || 'd'}`);
  }
  if (!showDays) {
    parts.push(`${formatNumber(hours + days * 24)}${t?.('h') || 'h'}`);
  } else {
    parts.push(`${formatNumber(hours)}${t?.('h') || 'h'}`);
  }
  parts.push(`${formatNumber(minutes)}${t?.('m') || 'm'}`);
  if (showSeconds) {
    parts.push(`${formatNumber(seconds)}${t?.('s') || 's'}`);
  }
  return {
    text: parts.join(' : '),
    isEnd: false,
    days,
    hours,
    minutes,
    seconds,
    hoursFormatted: formatNumber(hours),
    minutesFormatted: formatNumber(minutes),
    secondsFormatted: formatNumber(seconds),
    daysText: t?.('d') || 'd',
    hoursText: t?.('h') || 'h',
    minutesText: t?.('m') || 'm',
    secondsText: t?.('s') || 's'
  };
}
export const useCountdown = (date, t0) => {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    updateIntervalSeconds: t2,
    showSeconds: t3,
    padHoursMinutes: t4,
    showDays: t5
  } = options;
  const updateIntervalSeconds = t2 === undefined ? 60 : t2;
  const showSeconds = t3 === undefined ? false : t3;
  const padHoursMinutes = t4 === undefined ? false : t4;
  const showDays = t5 === undefined ? true : t5;
  const _t = useTranslations("timer");
  let t6;
  if ($[2] !== _t || $[3] !== date || $[4] !== padHoursMinutes || $[5] !== showDays || $[6] !== showSeconds) {
    t6 = () => formatCountdown(date, showSeconds, padHoursMinutes, showDays, _t);
    $[2] = _t;
    $[3] = date;
    $[4] = padHoursMinutes;
    $[5] = showDays;
    $[6] = showSeconds;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const [countdown, setCountdown] = useState(t6);
  let t7;
  if ($[8] !== _t || $[9] !== date || $[10] !== padHoursMinutes || $[11] !== showDays || $[12] !== showSeconds || $[13] !== updateIntervalSeconds) {
    t7 = () => {
      const update = () => {
        const result = formatCountdown(date, showSeconds, padHoursMinutes, showDays, _t);
        setCountdown(result);
        return result;
      };
      const result_0 = update();
      if (result_0.isEnd) {
        return;
      }
      const intervalId = window.setInterval(() => {
        const res = update();
        if (res.isEnd) {
          clearInterval(intervalId);
        }
      }, updateIntervalSeconds * 1000);
      return () => clearInterval(intervalId);
    };
    $[8] = _t;
    $[9] = date;
    $[10] = padHoursMinutes;
    $[11] = showDays;
    $[12] = showSeconds;
    $[13] = updateIntervalSeconds;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== _t || $[16] !== date || $[17] !== padHoursMinutes || $[18] !== showSeconds || $[19] !== updateIntervalSeconds) {
    t8 = [date, updateIntervalSeconds, showSeconds, padHoursMinutes, _t];
    $[15] = _t;
    $[16] = date;
    $[17] = padHoursMinutes;
    $[18] = showSeconds;
    $[19] = updateIntervalSeconds;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  useEffect(t7, t8);
  return countdown;
};